import { useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

const ScriptsLoader = () => {
    useEffect(() => {
        // Listen for real-time updates to scripts
        const unsubscribe = onSnapshot(doc(db, 'settings', 'scripts'), (docSnapshot) => {
            if (docSnapshot.exists()) {
                const { headerScripts, footerScripts } = docSnapshot.data();

                // Inject Header Scripts
                if (headerScripts) {
                    injectScripts(headerScripts, 'head');
                }

                // Inject Footer Scripts
                if (footerScripts) {
                    injectScripts(footerScripts, 'body');
                }
            }
        });

        return () => unsubscribe();
    }, []);

    const injectScripts = (htmlContent, location) => {
        // Create a temporary container to parse the HTML
        const container = document.createElement('div');
        container.innerHTML = htmlContent;

        // Find all script tags
        const scripts = container.querySelectorAll('script');

        // Find other tags (link, style, meta, etc.)
        // We generally focus on scripts, but GTM might have noscript iframes etc.
        // For simplicity and safety, we will handle scripts specifically to ensure they execute.
        // Simply setting innerHTML doesn't execute scripts in React/DOM.

        const target = location === 'head' ? document.head : document.body;

        // Remove previously injected scripts by this loader? 
        // Real-time updates might duplicate scripts if we don't clean up.
        // A simple way is to mark them with a class or data attribute.
        const existingScripts = document.querySelectorAll(`[data-injected-by="ScriptsLoader-${location}"]`);
        existingScripts.forEach(s => s.remove());

        // Helper to clone and append script so it executes
        const insertScript = (originalScript) => {
            const newScript = document.createElement('script');
            newScript.setAttribute('data-injected-by', `ScriptsLoader-${location}`);

            // Copy attributes
            Array.from(originalScript.attributes).forEach(attr => {
                newScript.setAttribute(attr.name, attr.value);
            });

            // Copy content
            if (originalScript.innerHTML) {
                newScript.innerHTML = originalScript.innerHTML;
            }

            target.appendChild(newScript);
        };

        // Handle Non-Script Nodes (comments, noscript, styles)
        // Note: innerHTML injection handles passive tags, but we need to manage cleanup.
        // Complex GTM snippets might be tricky. 
        // For now, let's focus on extracting SCRIPT tags and executing them.
        // Non-script tags (like comments or noscript) might be less critical for functionality but good for completeness.

        scripts.forEach(insertScript);
    };

    // NOTE: This loader primarily handles SCRIPT tags. 
    // Pure HTML (comments, noscript) might be lost with this specific implementation 
    // because we are manually reconstructing script tags to force execution.
    // If we want to support full HTML injection, we'd need a more robust parser or dangerous dangerouslySetInnerHTML
    // but dangerouslySetInnerHTML won't execute scripts.

    return null; // This component renders nothing visibly
};

export default ScriptsLoader;
